/**
 * Shared date helpers for the admin dashboard.
 *
 * The backend stores offer/feature/banner start & expiry dates as UTC-midnight
 * instants (e.g. 2026-09-01T00:00:00.000Z). Keeping every date input/round-trip
 * on that same UTC-midnight convention here avoids the classic `new Date(date)`
 * off-by-one and guarantees a date-only picker value maps to exactly the day the
 * admin selected, regardless of the browser's local timezone.
 */

/**
 * Convert a date-only input value ("YYYY-MM-DD", from an <input type="date">)
 * into a UTC-midnight ISO string for the backend. Returns null for empty input.
 *
 * NOTE: `new Date("YYYY-MM-DD")` is defined by the ECMAScript spec to parse as
 * UTC midnight, so date-only strings already land on midnight UTC. This helper
 * normalizes whatever shape is provided and guards invalid values.
 */
export function toIsoUtcMidnight(value) {
    if (value == null) return null;
    const str = String(value).trim();
    if (!str) return null;
    const date = new Date(str);
    return isNaN(date.getTime()) ? null : date.toISOString();
}

/**
 * Convert a stored ISO instant back to a "YYYY-MM-DD" value for an
 * <input type="date">. Because the backend stores UTC midnight, we read the
 * calendar day in UTC so we always round-trip to the exact day that was picked.
 * Returns '' for empty/invalid input.
 */
export function toDateKey(iso) {
    if (!iso) return '';
    const date = new Date(iso);
    return isNaN(date.getTime()) ? '' : date.toISOString().split('T')[0];
}

/**
 * Convention: today's date as "YYYY-MM-DD" (UTC), used as a safe default for
 * start dates that were never set.
 */
export function todayDateKey() {
    return toDateKey(new Date().toISOString());
}
